import { isAdmin } from '@/constants/roles';
import { Search } from 'lucide-react';
import { useContext, useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { toast } from 'sonner';

import {
    ChainlitContext,
    IThread,
    threadHistoryState,
    useConfig
} from '@chainlit/react-client';

import ThreadListItem from '@/components/chat/DMThreadListItem';
import { Input } from '@/components/ui/input';

import { usePinnedThreads } from '@/hooks/DMusePinnedThreads';
import { useAuth } from '@/hooks/useAuth';
import { MobileNavTrigger } from '@/components/ScheduledAgents/MobileNavTrigger';

export default function DMOrganizationThreads() {
    const setThreadHistory = useSetRecoilState(threadHistoryState);

    useEffect(() => {
        setThreadHistory((prev) => {
            if (prev?.currentThreadId === '__none__') return prev;
            return { ...prev, currentThreadId: '__none__' };
        });
    }, [setThreadHistory]);
    const { threads: allThreads, isLoading, refresh } = usePinnedThreads();
    const apiClient = useContext(ChainlitContext);
    const { user } = useAuth();
    const { config } = useConfig();
    const [filteredThreads, setFilteredThreads] = useState<IThread[]>([]);
    const [searchQuery, setSearchQuery] = useState('');
    const userIsAdmin = isAdmin(user?.metadata?.organization_roles);

    // Same fail-closed Scheduled Jobs gate as LeftSidebar's nav button — a
    // user without access to the feature shouldn't see Deep Intelligence
    // threads surface here either, even if one was pinned to the org before
    // (or after) their access was toggled off.
    const scheduledJobsEnabled = config?.features?.scheduled_jobs_enabled === true;
    const threads = scheduledJobsEnabled
        ? allThreads
        : allThreads.filter((thread) => !thread.isDeepIntelligence);

    useEffect(() => {
        const handlePinChanged = () => {
            refresh();
        };

        window.addEventListener('thread-pin-changed', handlePinChanged);
        return () => {
            window.removeEventListener('thread-pin-changed', handlePinChanged);
        };
    }, [refresh]);

    const handleUnpin = async (threadId: string) => {
        try {
            await apiClient.updateThread(threadId, { isPinned: false });
            await refresh();

            window.dispatchEvent(
                new CustomEvent('thread-pin-changed', {
                    detail: { threadId }
                })
            );

            toast.success('Thread unpinned');
        } catch (err) {
            console.error('Failed to unpin thread:', err);
            toast.error('Failed to unpin thread');
        }
    };

    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredThreads(threads);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = threads.filter(
            (thread) =>
                thread.name?.toLowerCase().includes(query) ||
                thread.userIdentifier?.toLowerCase().includes(query)
        );
        setFilteredThreads(filtered);
    }, [searchQuery, threads]);

    return (
        <div className="relative flex flex-col bg-background size-full">
            <MobileNavTrigger />
            <div className="mx-auto px-3 sm:px-12 pt-8 sm:pt-12 pb-6 sm:pb-8 w-full max-w-agentPageWidth">
                <div className="flex flex-col gap-3 sm:gap-4">
                    <div className="flex flex-col gap-4 sm:gap-6">
                        <div className="flex justify-between items-start gap-2">
                            <div className="flex flex-col flex-1 gap-1">
                                <h1 className="font-medium text-foreground text-2xl lg:text-3xl">
                                    Organization Chats
                                </h1>
                                <p className="text-muted-foreground text-sm sm:text-lg">
                                    Browse through all shared chats saved within
                                    your organization
                                </p>
                            </div>
                        </div>

                        <div className="relative">
                            <Search className="top-1/2 left-3 absolute size-4 sm:size-5 text-muted-foreground -translate-y-1/2" />
                            <Input
                                type="text"
                                placeholder="Search on thread content..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 sm:pl-10 h-9 sm:h-10"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 mx-auto px-3 sm:px-12 pb-6 sm:pb-8 w-full max-w-agentPageWidth overflow-auto">
                <div className="flex flex-col">
                    {isLoading ? (
                        <div className="flex justify-center items-center py-8 text-muted-foreground text-sm">
                            Loading threads...
                        </div>
                    ) : filteredThreads.length === 0 ? (
                        <div className="flex justify-center items-center py-8 text-muted-foreground text-sm">
                            {searchQuery
                                ? 'No threads match your search'
                                : 'No organization chats found'}
                        </div>
                    ) : (
                        filteredThreads.map((thread, index) => (
                            <ThreadListItem
                                key={thread.id}
                                thread={thread}
                                currentUserIdentifier={user?.identifier}
                                showBorder={index < filteredThreads.length - 1}
                                onUnpin={handleUnpin}
                                userIsAdmin={userIsAdmin}
                            />
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
